import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Banner,
  Badge,
  Text,
  BlockStack,
  InlineStack,
  DataTable,
  SkeletonBodyText,
  Spinner,
  List,
  Icon,
  EmptyState,
} from '@shopify/polaris';
import { PhoneIcon } from '@shopify/polaris-icons';
import { useShopifyFetch } from '../hooks/useShopifyFetch';
import { useToast } from '../context/ToastContext';

/** Preserve the App Bridge host/shop query string on internal navigation. */
const withSearch = (path: string) => `${path}${window.location.search}`;

interface ActivationNumber {
  phone_number?: string;
  display?: string;
  is_trial?: boolean;
  assigned_at?: string;
}

interface CallStatus {
  connected: boolean;
  brand_id: string | null;
  state: string | null;
  tracking_number: ActivationNumber | null;
  total_calls: number;
  first_call_at: string | null;
  next_step: string | null;
  provisioning_error?: string | null;
}

interface TrackingNumber {
  id: string;
  phone_number: string;
  formatted_number?: string;
  status: string;
  is_trial?: boolean;
  campaign_name?: string | null;
  call_handling_mode?: string | null;
  sms_enabled?: boolean;
  country_code?: string | null;
  created_at?: string;
}

interface CallNumbers {
  connected: boolean;
  numbers: TrackingNumber[];
  total: number;
  hint: string | null;
}

const STATE_TONE: Record<string, 'success' | 'attention' | 'info' | 'warning'> = {
  activated: 'success',
  awaiting_first_call: 'info',
  needs_number: 'attention',
  not_connected: 'warning',
  unavailable: 'warning',
};

const STATE_LABEL: Record<string, string> = {
  activated: 'Active',
  awaiting_first_call: 'Awaiting first call',
  needs_number: 'No number yet',
  not_connected: 'Not connected',
  unavailable: 'Unavailable',
};

const numberStatusTone = (status: string): 'success' | 'attention' | 'critical' | 'info' => {
  const s = (status || '').toLowerCase();
  if (s === 'active' || s === 'assigned') return 'success';
  if (s === 'available' || s === 'reserved') return 'info';
  if (s === 'released' || s === 'suspended') return 'critical';
  return 'attention';
};

export default function Calls() {
  const { get, post } = useShopifyFetch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery<CallStatus>({
    queryKey: ['calls', 'status'],
    queryFn: () => get('/api/calls/status'),
  });

  const connected = !!status?.connected;

  const { data: numbersData, isLoading: numbersLoading } = useQuery<CallNumbers>({
    queryKey: ['calls', 'numbers'],
    queryFn: () => get('/api/calls/numbers'),
    enabled: connected,
  });

  const provisionMutation = useMutation({
    mutationFn: () =>
      post<{ status: string; state: string | null; tracking_number: ActivationNumber | null }>(
        '/api/calls/numbers/provision'
      ),
    onSuccess: (data) => {
      if (data.tracking_number?.phone_number || data.status === 'provisioned') {
        showToast('Trial tracking number enabled.');
      } else {
        showToast('Call tracking is being set up. Check back shortly.');
      }
      queryClient.invalidateQueries({ queryKey: ['calls'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error ? error.message : 'Could not enable call tracking. Please try again.',
        { error: true }
      );
    },
  });

  // ----- Not connected to an Affilync brand -----
  if (!statusLoading && !connected) {
    return (
      <Page title="Call Tracking" subtitle="Pay-per-call tracking for your store">
        <Layout>
          <Layout.Section>
            <Banner
              tone="warning"
              title="Connect your Affilync account"
              action={{
                content: 'Go to Settings',
                onAction: () => navigate(withSearch('/settings')),
              }}
            >
              <p>
                {status?.next_step ||
                  'Connect your Affilync brand account in Settings to enable pay-per-call tracking.'}
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (statusError) {
    return (
      <Page title="Call Tracking">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load call tracking status. Please try again.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const numbers = numbersData?.numbers || [];
  const stateKey = status?.state || 'unavailable';
  const needsNumber =
    connected && !numbersLoading && numbers.length === 0 && stateKey !== 'unavailable';

  return (
    <Page
      title="Call Tracking"
      subtitle="Pay-per-call tracking for your store — no separate app, no UUIDs to copy"
      titleMetadata={
        <Badge tone={STATE_TONE[stateKey] || 'attention'}>
          {STATE_LABEL[stateKey] || 'Unknown'}
        </Badge>
      }
      primaryAction={
        needsNumber
          ? {
              content: 'Enable call tracking',
              onAction: () => provisionMutation.mutate(),
              loading: provisionMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Status summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Status
                </Text>
              </InlineStack>
              {statusLoading ? (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner accessibilityLabel="Loading status" size="small" />
                  <Text as="span" variant="bodyMd" tone="subdued">
                    Loading call tracking status…
                  </Text>
                </InlineStack>
              ) : (
                <InlineStack gap="600" wrap>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Total calls tracked
                    </Text>
                    <Text as="p" variant="headingLg">
                      {(status?.total_calls || 0).toLocaleString()}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      First call
                    </Text>
                    <Text as="p" variant="headingLg">
                      {status?.first_call_at
                        ? new Date(status.first_call_at).toLocaleDateString()
                        : '—'}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Tracking numbers
                    </Text>
                    <Text as="p" variant="headingLg">
                      {numbersLoading ? '…' : numbers.length.toLocaleString()}
                    </Text>
                  </BlockStack>
                </InlineStack>
              )}

              {status?.next_step && (
                <Banner tone={stateKey === 'unavailable' ? 'warning' : 'info'}>
                  <p>{status.next_step}</p>
                </Banner>
              )}

              {status?.provisioning_error && (
                <Banner tone="critical">
                  <p>{status.provisioning_error}</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Tracking numbers */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Tracking numbers
              </Text>
              {numbersLoading ? (
                <SkeletonBodyText lines={4} />
              ) : numbers.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['Number', 'Status', 'Type', 'Campaign', 'Handling']}
                  rows={numbers.map((n) => [
                    n.formatted_number || n.phone_number,
                    <Badge key={`${n.id}-status`} tone={numberStatusTone(n.status)}>
                      {n.status || 'unknown'}
                    </Badge>,
                    n.is_trial ? 'Trial' : 'Standard',
                    n.campaign_name || '—',
                    n.call_handling_mode || '—',
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No tracking numbers yet"
                  action={{
                    content: 'Enable call tracking',
                    onAction: () => provisionMutation.mutate(),
                    loading: provisionMutation.isPending,
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    {numbersData?.hint ||
                      'Enable call tracking to claim your free trial number and start attributing phone calls to affiliates.'}
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* How it works */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <Icon source={PhoneIcon} tone="base" />
                <Text as="h2" variant="headingMd">
                  How pay-per-call works
                </Text>
              </InlineStack>
              <List type="number">
                <List.Item>
                  Enable call tracking to claim a free trial tracking number for your brand.
                </List.Item>
                <List.Item>
                  Add the Affilync call widget (Dynamic Number Insertion) to your storefront theme.
                  It automatically detects your connected brand — no IDs to copy or type.
                </List.Item>
                <List.Item>
                  Visitors who arrive from an affiliate see the tracked number; their calls are
                  attributed to that affiliate for pay-per-call payouts.
                </List.Item>
              </List>
              <Text as="p" variant="bodySm" tone="subdued">
                Additional or paid numbers (local, toll-free, or other countries) are managed in the
                full Affilync call-tracking product.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
